
import { Button } from "@/components/ui/button"
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useToast } from "@/hooks/use-toast"
import { downloadCSV } from "@/utils/csvExport"
import { useQueryClient } from "@tanstack/react-query"
import { Download } from "lucide-react"
import { useState } from "react"
import { AssessmentsTable } from "./AssessmentsTable"
import { EmptyState } from "./EmptyState"
import { useAssessmentDeletion } from "./hooks/useAssessmentDeletion"
import { useAssessments } from "./hooks/useAssessments"
import { useAuthCheck } from "./hooks/useAuthCheck"
import { useStatusUpdate } from "./hooks/useStatusUpdate"

type StatusType = "draft" | "completed" | "prescribed" | "denied"

export const StatusTabs = () => {
  const { authChecked, isProvider } = useAuthCheck()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [currentPages, setCurrentPages] = useState<Record<StatusType, number>>({
    completed: 0,
    prescribed: 0,
    denied: 0,
    draft: 0
  })
  const [activeTab, setActiveTab] = useState<StatusType>("completed")
  
  // Fetch data for the current active tab only
  const { 
    data, 
    isLoading
  } = useAssessments(isProvider, authChecked, currentPages[activeTab], activeTab)

  const { handleStatusUpdate } = useStatusUpdate()
  
  // Add deletion functionality
  const handleDeleteSuccess = () => {
    // Invalidate and refetch assessments after deletion
    queryClient.invalidateQueries({ queryKey: ["provider-assessments"] })
    toast({
      title: "Assessment Deleted",
      description: "The draft assessment has been successfully deleted.",
    })
  }
  
  const { deleteAssessment, isDeleting } = useAssessmentDeletion(handleDeleteSuccess)

  const assessments = data?.assessments ?? []

  const handleDownload = async (status: string) => {
    const timestamp = new Date().toISOString().split('T')[0];
    await downloadCSV(status.toLowerCase() as 'draft' | 'completed' | 'prescribed' | 'denied', 
      `${status}-assessments-${timestamp}.csv`);
  };

  const handlePageChange = (page: number) => {
    setCurrentPages(prev => ({
      ...prev,
      [activeTab]: page
    }))
  }

  const handleTabChange = (value: string) => {
    setActiveTab(value as StatusType)
  }

  const renderPagination = () => {
    if (!data || data.totalPages <= 1) return null

    const { currentPage, totalPages, hasNextPage, hasPreviousPage } = data

    return (
      <div className="mt-6">
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious 
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  if (hasPreviousPage) handlePageChange(currentPage - 1)
                }}
                className={!hasPreviousPage ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
            
            {/* Show page numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number
              if (totalPages <= 5) {
                pageNum = i
              } else if (currentPage <= 2) {
                pageNum = i
              } else if (currentPage >= totalPages - 3) {
                pageNum = totalPages - 5 + i
              } else {
                pageNum = currentPage - 2 + i
              }

              return (
                <PaginationItem key={pageNum}>
                  <PaginationLink
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      handlePageChange(pageNum)
                    }}
                    isActive={currentPage === pageNum}
                    className="cursor-pointer"
                  >
                    {pageNum + 1}
                  </PaginationLink>
                </PaginationItem>
              )
            })}

            {totalPages > 5 && currentPage < totalPages - 3 && (
              <PaginationItem>
                <PaginationEllipsis />
              </PaginationItem>
            )}
            
            <PaginationItem>
              <PaginationNext 
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  if (hasNextPage) handlePageChange(currentPage + 1)
                }}
                className={!hasNextPage ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
        
        <div className="text-center text-sm text-muted-foreground mt-2">
          Showing page {currentPage + 1} of {totalPages} ({data.totalCount} total {activeTab} assessments)
        </div>
      </div>
    )
  }

  if (isLoading) {
    return <div>Loading...</div>
  }

  if (!data || data.totalCount === 0) {
    return <EmptyState />
  }

  const renderTabHeader = (label: string, status: StatusType) => (
    <div className="relative flex items-center gap-2">
      <span>{label}</span>
      <Button
        variant="ghost"
        size="sm"
        className="ml-2"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleDownload(label);
        }}
      >
        <Download className="h-4 w-4" />
      </Button>
    </div>
  );

  return (
    <div className="w-full">
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="completed">
            {renderTabHeader("Completed", "completed")}
          </TabsTrigger>
          <TabsTrigger value="prescribed">
            {renderTabHeader("Prescribed", "prescribed")}
          </TabsTrigger>
          <TabsTrigger value="denied">
            {renderTabHeader("Denied", "denied")}
          </TabsTrigger>
          <TabsTrigger value="draft">
            {renderTabHeader("Draft", "draft")}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="completed">
          <AssessmentsTable 
            assessments={assessments} 
            showActions={true}
            onStatusUpdate={handleStatusUpdate}
            showDenialReason={false}
          />
        </TabsContent>
        <TabsContent value="prescribed">
          <AssessmentsTable 
            assessments={assessments} 
            showActions={false}
            showDenialReason={false}
          />
        </TabsContent>
        <TabsContent value="denied">
          <AssessmentsTable 
            assessments={assessments} 
            showActions={true}
            onStatusUpdate={handleStatusUpdate}
            showDenialReason={true}
          />
        </TabsContent>
        <TabsContent value="draft">
          <AssessmentsTable 
            assessments={assessments} 
            showActions={true}
            showDenialReason={false}
            onDeleteDraft={deleteAssessment}
          />
        </TabsContent>
      </Tabs>
      
      {renderPagination()}
    </div>
  )
}
